<template>
    <v-row>
        <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
            <v-card v-if="dialog">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title v-if="mode === 'one' && position">
                        Change Log &mdash; {{ position.stock }} | {{ position.company }}
                    </v-toolbar-title>
                    <v-toolbar-title v-else>Change Log &mdash; All Positions</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-data-table
                        :loading="loading"
                        :headers="tableHeaders"
                        :items="entries"
                        :server-items-length="totalCount"
                        :options.sync="options"
                        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                        disable-sort
                        show-expand
                        single-expand
                        :expanded.sync="expanded"
                        item-key="id"
                        mobile-breakpoint="100"
                        class="elevation-1 datat mt-4"
                    >
                        <template v-slot:[`item.changed_at`]="{ item }">
                            {{ new Date(item.changed_at).toLocaleDateString('en-IN') }}
                            {{ new Date(item.changed_at).toLocaleTimeString('en-IN') }}
                        </template>
                        <template v-slot:[`item.stock`]="{ item }">
                            {{ item.stock }}
                            <div class="grey--text text-caption">{{ item.company }}</div>
                        </template>
                        <template v-slot:[`item.operation`]="{ item }">
                            <v-chip :color="opColor(item)" text-color="white" small>{{ opLabel(item) }}</v-chip>
                        </template>
                        <template v-slot:[`item.changed_by_email`]="{ item }">
                            {{ item.changed_by_email || 'System / direct SQL' }}
                        </template>
                        <template v-slot:[`item.changes`]="{ item }">
                            <div v-if="item.operation == 'UPDATE'" class="text-left py-1">
                                <div v-for="diff in diffList(item).slice(0, 4)" :key="diff.label">
                                    <strong>{{ diff.label }}</strong>: {{ diff.oldVal }} &rarr; {{ diff.newVal }}
                                </div>
                                <div v-if="diffList(item).length > 4" class="grey--text">
                                    +{{ diffList(item).length - 4 }} more
                                </div>
                            </div>
                            <div v-else-if="item.operation == 'EMAIL'" class="text-left py-1">
                                <div>{{ item.new_data.subject }}</div>
                                <div class="grey--text text-caption">
                                    {{ (item.new_data.recipients || []).length }} recipient(s)
                                    <span v-if="(item.new_data.failed || []).length"> · {{ item.new_data.failed.length }} failed</span>
                                </div>
                            </div>
                            <div v-else-if="item.operation == 'INSERT'">{{ item.entity == 'research' ? 'Research added' : 'Position created' }}</div>
                            <div v-else>{{ item.entity == 'research' ? 'Research deleted' : 'Position deleted' }}</div>
                        </template>
                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length" class="py-3 text-left">
                                <div v-if="item.operation == 'UPDATE'">
                                    <div v-for="diff in diffList(item)" :key="diff.label">
                                        <strong>{{ diff.label }}</strong>: {{ diff.oldVal }} &rarr; {{ diff.newVal }}
                                    </div>
                                </div>
                                <div v-else-if="item.operation == 'EMAIL'">
                                    <div class="mb-2"><strong>{{ item.new_data.subject }}</strong></div>
                                    <div class="mb-2">
                                        <v-chip v-for="r in item.new_data.recipients" :key="r" x-small class="mr-1 mb-1">{{ r }}</v-chip>
                                    </div>
                                    <div v-if="(item.new_data.failed || []).length" class="mb-2 red--text">
                                        <div v-for="f in item.new_data.failed" :key="f.email">Failed: {{ f.email }} — {{ f.error }}</div>
                                    </div>
                                    <div class="email-body-view pa-3" v-html="item.new_data.html_body"></div>
                                </div>
                                <vue-json-pretty v-else :data="item.new_data || item.old_data" :show-line="true" />
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { supabase } from '../supabase'
import VueJsonPretty from 'vue-json-pretty'

const FALLBACK_LABELS = {
    id: 'ID',
    created_at: 'Created At',
    stock: 'Stock',
    company: 'Company',
    industry: 'Industry',
    mcap: 'Market Cap',
    entry_date: 'Entry Date',
    exit_date: 'Exit Date',
    min_entry: 'Min Entry',
    max_entry: 'Max Entry',
    current_view: 'Current View',
    cmp: 'CMP/CL',
    returns: 'Return',
    package: 'Package',
    research_data: 'Research Notes',
    pdf: 'Report PDF',
    position_id: 'Position ID',
}

const DATE_FIELDS = ['entry_date', 'exit_date', 'created_at']

export default {
    name: 'PositionHistory',
    components: { VueJsonPretty },
    data() {
        return {
            dialog: false,
            mode: 'all',
            position: null,
            entries: [],
            totalCount: 0,
            loading: false,
            options: { page: 1, itemsPerPage: 25 },
            labelMap: null,
            expanded: [],
        }
    },
    computed: {
        tableHeaders() {
            const headers = [
                { text: 'When', value: 'changed_at', align: 'center' },
                { text: 'Stock', value: 'stock', align: 'center' },
                { text: 'Action', value: 'operation', align: 'center' },
                { text: 'Changed By', value: 'changed_by_email', align: 'center' },
                { text: 'Changes', value: 'changes', align: 'center' },
                { text: '', value: 'data-table-expand' },
            ]
            return this.mode === 'one' ? headers.filter(h => h.value !== 'stock') : headers
        },
    },
    watch: {
        options: {
            handler() {
                if (this.dialog) this.fetchEntries()
            },
            deep: true,
        },
    },
    methods: {
        openForPosition(item) {
            this.mode = 'one'
            this.position = item
            this.openDialog()
        },
        openAll() {
            this.mode = 'all'
            this.position = null
            this.openDialog()
        },
        openDialog() {
            this.entries = []
            this.totalCount = 0
            this.expanded = []
            this.dialog = true
            if (this.options.page !== 1) {
                this.options.page = 1 // watcher triggers fetchEntries
            } else {
                this.fetchEntries()
            }
        },
        async fetchEntries() {
            this.loading = true
            await this.loadLabels()
            const from = (this.options.page - 1) * this.options.itemsPerPage
            let query = supabase
                .from('positions_audit')
                .select('*', { count: 'exact' })
                .order('changed_at', { ascending: false })
                .order('id', { ascending: false })
                .range(from, from + this.options.itemsPerPage - 1)
            if (this.mode === 'one') {
                query = query.eq('position_id', this.position.id)
            }
            const { data, error, count } = await query
            if (error) {
                this.$store.dispatch('createAlert', { type: 'error', message: error.message })
            } else {
                this.entries = data
                this.totalCount = count || 0
            }
            this.loading = false
        },
        async loadLabels() {
            if (this.labelMap) return
            const { data, error } = await supabase.from('table_map').select('cno, label')
            if (error) return // fall back to FALLBACK_LABELS / raw keys
            const map = {}
            data.forEach(row => { map[row.cno] = row.label })
            this.labelMap = map
        },
        diffList(entry) {
            const keys = entry.changed_fields && entry.changed_fields.length
                ? entry.changed_fields
                : Object.keys(entry.new_data || {}).filter(
                    k => JSON.stringify((entry.old_data || {})[k]) !== JSON.stringify((entry.new_data || {})[k]))
            return keys.map(k => ({
                label: this.fieldLabel(k),
                oldVal: this.formatValue(k, entry.old_data ? entry.old_data[k] : null),
                newVal: this.formatValue(k, entry.new_data ? entry.new_data[k] : null),
            }))
        },
        fieldLabel(key) {
            return (this.labelMap && this.labelMap[key]) || FALLBACK_LABELS[key] || key
        },
        opLabel(item) {
            if (item.operation == 'EMAIL') return 'Email Sent'
            const base = { INSERT: 'Added', UPDATE: 'Updated', DELETE: 'Deleted' }[item.operation] || item.operation
            return item.entity == 'research' ? 'Research ' + base : base
        },
        opColor(item) {
            return { INSERT: 'green', UPDATE: 'orange', DELETE: 'red', EMAIL: 'blue' }[item.operation] || 'grey'
        },
        formatValue(key, val) {
            if (val === null || val === undefined || val === '') return '—'
            if (DATE_FIELDS.includes(key)) return new Date(val).toLocaleDateString('en-IN')
            if (key === 'returns') return (val * 100).toFixed(1) + '%'
            const str = String(val)
            // research_data holds full markdown; keep diffs readable
            return str.length > 120 ? str.slice(0, 120) + '…' : str
        },
    },
}
</script>

<style scoped>
.email-body-view {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    max-height: 300px;
    overflow-y: auto;
}
</style>
